import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SettingsIcon from "@mui/icons-material/Settings";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CategoryIcon from "@mui/icons-material/Category";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import AssessmentIcon from "@mui/icons-material/Assessment";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import BookCategory from "./BookCategory";
import BookCollectionType from "./BookCollectionType";
import UserType from "./UserType";
import Author from "./Author";
import Publisher from "./Publisher";
import Vendor from "./Vendor";
import Rules from "./Rules";
import Book from "./Book";
import Issue from "./Issue";
import Return from "./Return";
import FineReport from "./FineReport";

function Library() {
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("home");

  // 0 = UPLOAD
  // 1 = SETUP
  // 2 = ACTIONS
  // 3 = REPORTS
  const [tabValue, setTabValue] = useState(2);

  const navigate = useNavigate();

  useEffect(() => {
    setLoading(false);
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleNavigate = (path) => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      navigate("/authentication/sign-in");
    } else {
      setActiveView(path);
    }
  };

  const handleBack = () => {
    setActiveView("home");
  };

  // Reusable menu button
  // Reusable colorful menu button
  const MenuCard = ({ title, icon, path, color = "#2196F3" }) => (
    <Grid item xs={12} sm={6} md={4} lg={2.4}>
      <MDButton
        fullWidth
        variant="gradient"
        size="large"
        startIcon={icon}
        sx={{
          height: 90,
          fontSize: "0.85rem",
          fontWeight: 700,
          letterSpacing: "0.2px",
          whiteSpace: "normal",
          lineHeight: 1.2,
          borderRadius: "12px",
          color: "#FFFFFF !important",
          background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
          boxShadow: `0 4px 12px ${color}40`,
          transition: "all 0.25s ease",
          "&:hover": {
            background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`,
            transform: "translateY(-4px)",
            boxShadow: `0 8px 20px ${color}60`,
          },
          "& .material-icons-round, & .material-icons": {
            fontSize: "28px",
            color: "#FFFFFF !important",
          },
          "& .MuiSvgIcon-root": {
            fontSize: "28px",
            color: "#FFFFFF",
          },
        }}
        onClick={() => handleNavigate(path)}
      >
        {title}
      </MDButton>
    </Grid>
  );

  MenuCard.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.node,
    path: PropTypes.string.isRequired,
    color: PropTypes.string,
  };

  const renderContent = () => {
    switch (activeView) {
      // =========================
      // UPLOAD
      // =========================
      case "book":
        return <Book />;

      // =========================
      // SETUP
      // =========================
      case "bookcategory":
        return <BookCategory />;

      case "bookcollectiontype":
        return <BookCollectionType />;

      case "usertype":
        return <UserType />;

      case "author":
        return <Author />;

      case "publisher":
        return <Publisher />;

      case "vendor":
        return <Vendor />;

      case "rules":
        return <Rules />;

      // =========================
      // ACTIONS
      // =========================
      case "issue":
        return <Issue />;

      case "return":
        return <Return />;

      // =========================
      // REPORTS
      // =========================
      case "finereport":
        return <FineReport />;

      // =========================
      // HOME
      // =========================
      default:
        return (
          <>
            {/* =========================
                TABS
            ========================= */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
                TabIndicatorProps={{
                  sx: {
                    backgroundColor: ["#2196F3", "#4CAF50", "#FF9800", "#9C27B0"][tabValue],
                    height: 5,
                    borderRadius: "5px 5px 0 0",
                  },
                }}
              >
                {[
                  {
                    label: "UPLOAD",
                    icon: <CloudUploadIcon />,
                    color: "#2196F3",
                    bgColor: "rgba(33, 150, 243, 0.10)",
                  },
                  {
                    label: "SETUP",
                    icon: <SettingsIcon />,
                    color: "#4CAF50",
                    bgColor: "rgba(76, 175, 80, 0.10)",
                  },
                  {
                    label: "ACTIONS",
                    icon: <TouchAppIcon />,
                    color: "#FF9800",
                    bgColor: "rgba(255, 152, 0, 0.12)",
                  },
                  {
                    label: "REPORTS",
                    icon: <AssessmentIcon />,
                    color: "#9C27B0",
                    bgColor: "rgba(156, 39, 176, 0.10)",
                  },
                ].map((tab, index) => (
                  <Tab
                    key={tab.label}
                    icon={tab.icon}
                    iconPosition="top"
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.2,
                        }}
                      >
                        {tab.label}

                        {tabValue === index && (
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: 30,
                              mt: 0.2,
                              color: tab.color,
                            }}
                          />
                        )}
                      </Box>
                    }
                    sx={{
                      minHeight: 105,
                      fontSize: "1rem",
                      fontWeight: 700,
                      py: 1.2,
                      color: "#757575",
                      transition: "all 0.25s ease",
                      "& .MuiSvgIcon-root": {
                        fontSize: 30,
                        color: tab.color,
                        transition: "transform 0.25s ease",
                      },
                      "&:hover": {
                        backgroundColor: tab.bgColor,
                        "& .MuiSvgIcon-root": {
                          transform: "scale(1.12)",
                        },
                      },
                      "&.Mui-selected": {
                        color: tab.color,
                        fontWeight: 800,
                        backgroundColor: tab.bgColor,
                      },
                    }}
                  />
                ))}
              </Tabs>
            </Box>
            {/* =========================
                UPLOAD
            ========================= */}
            {tabValue === 0 && (
              <Grid container spacing={2}>
                <MenuCard title="BOOKS" icon={<LibraryBooksIcon />} path="book" color="#2196F3" />
              </Grid>
            )}

            {/* =========================
                SETUP
            ========================= */}
            {tabValue === 1 && (
              <Grid container spacing={2}>
                <MenuCard
                  title="BOOK CATEGORY"
                  icon={<CategoryIcon />}
                  path="bookcategory"
                  color="#4CAF50"
                />

                <MenuCard
                  title="BOOK COLLECTION TYPE"
                  icon={<MenuBookIcon />}
                  path="bookcollectiontype"
                  color="#009688"
                />

                <MenuCard
                  title="USER TYPE"
                  icon={<CategoryIcon />}
                  path="usertype"
                  color="#8BC34A"
                />

                <MenuCard title="AUTHORS" icon={<CategoryIcon />} path="author" color="#00BCD4" />

                <MenuCard
                  title="PUBLISHERS"
                  icon={<CategoryIcon />}
                  path="publisher"
                  color="#3F51B5"
                />

                <MenuCard title="VENDORS" icon={<CategoryIcon />} path="vendor" color="#607D8B" />

                <MenuCard title="RULES" icon={<CategoryIcon />} path="rules" color="#2E7D32" />
              </Grid>
            )}

            {/* =========================
                ACTIONS
            ========================= */}
            {tabValue === 2 && (
              <Grid container spacing={2}>
                <MenuCard title="ISSUE BOOK" icon={<HowToRegIcon />} path="issue" color="#FF9800" />

                <MenuCard
                  title="RETURN BOOK"
                  icon={<AssignmentReturnIcon />}
                  path="return"
                  color="#F4511E"
                />
              </Grid>
            )}

            {/* =========================
                REPORTS
            ========================= */}
            {tabValue === 3 && (
              <Grid container spacing={2}>
                <MenuCard
                  title="BOOKS FINE REPORT"
                  icon={<AssessmentIcon />}
                  path="finereport"
                  color="#9C27B0"
                />
              </Grid>
            )}
          </>
        );
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={2} pb={3}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress color="info" />
          </MDBox>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                {/* HEADER - Matching Notifications card design */}
                <MDBox
                  mx={2}
                  mt={-1}
                  py={1}
                  px={2}
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ color: "#FFFFFF" }}>local_library</Icon>

                    <MDTypography variant="h6" color="white">
                      LIBRARY
                    </MDTypography>
                  </MDBox>

                  {activeView !== "home" ? (
                    <MDButton
                      variant="contained"
                      onClick={handleBack}
                      startIcon={<Icon>arrow_back</Icon>}
                      sx={{
                        mb: 0,
                        color: "#FFFFFF",
                        backgroundColor: "rgba(255,255,255,0.18)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.28)",
                        },
                      }}
                    >
                      Back to Library
                    </MDButton>
                  ) : (
                    <MDButton
                      variant="contained"
                      onClick={() => navigate("/dashboard")}
                      startIcon={<Icon>dashboard</Icon>}
                      sx={{
                        mb: 0,
                        color: "#FFFFFF !important",
                        backgroundColor: "rgba(255,255,255,0.18) !important",
                        boxShadow: "none !important",
                        "& .material-icons": {
                          color: "#FFFFFF !important",
                        },
                        "&:hover": {
                          color: "#FFFFFF !important",
                          backgroundColor: "rgba(255,255,255,0.28) !important",
                          boxShadow: "none !important",
                        },
                        "&:focus": {
                          color: "#FFFFFF !important",
                          backgroundColor: "rgba(255,255,255,0.18) !important",
                        },
                        "&:active": {
                          backgroundColor: "rgba(255,255,255,0.28) !important",
                        },
                      }}
                    >
                      Dashboard
                    </MDButton>
                  )}
                </MDBox>

                {/* CONTENT */}
                <MDBox p={1}>{renderContent()}</MDBox>
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>

      <MDBox display="flex" flexDirection="column" minHeight="50vh" />

      <Footer />
    </DashboardLayout>
  );
}

export default Library;
